import { Close } from '@mui/icons-material';
import {
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import DocumentInputMasked from '../../../../components/DocumentInputMasked';
import PhoneInputMasked from '../../../../components/PhoneInputMasked';
import SelectClub from '../../../../components/SelectClub';
import SelectPlan from '../../../../components/SelectPlan';
import useDialog from '../../../../hooks/useDialog';
import useErrors from '../../../../hooks/useErrors';
import server from '../../../../services/server';

export default function ProposalForm({ mutate }) {
    const { setErrors, enqueueSnackbar } = useErrors();
    const navigation = useNavigate();
    const { open, handleClose } = useDialog('#contrato');

    const { values, handleBlur, handleSubmit, getFieldProps, setFieldValue, errors, isSubmitting } = useFormik({
        initialValues: {
            document: '',
            tradeName: '',
            contractorName: '',
            contractorPhone: '',
            contractorPosition: '',
            contractorEmail: '',
            planId: '',
            clubId: '',
        },
        validationSchema: Yup.object().shape({
            contractorName: Yup.string().required('Nome do contato é obrigatório').min(3, 'Nome do contato deve ter pelo menos 3 caracteres'),
            contractorEmail: Yup.string().email('Email deve ter um formato válido').required('Email é obrigatório'),
        }),
        onSubmit: async (formValues, { setSubmitting }) => {
            try {
                setSubmitting(true);
                const { data } = await server.post(`subscription`, formValues);
                enqueueSnackbar('Proposta criada com sucesso');
                await mutate();
                setSubmitting(false);
                handleClose();
                navigation(`/dashboard/propostas/${data.data.uuid}`, { replace: true });
            } catch (error) {
                setSubmitting(false);
                setErrors(error);
            }
        },
    });

    const handleClubChange = async ({ target: { value } }) => {
        try {
            await setFieldValue('clubId', value);

            if (!value) return;

            const { data } = await server.get(`clubs/${value}`);
            const club = data?.data || data;

            if (club) {
                setFieldValue('document', club.document || '');
                setFieldValue('tradeName', club.name || '');
                setFieldValue('contractorName', club.responsibleName || '');
                setFieldValue('contractorPhone', club.responsiblePhone || '');
                setFieldValue('contractorPosition', club.responsiblePosition || '');
                setFieldValue('contractorEmail', club.responsibleEmail || '');
            }
        } catch (error) {
            setErrors(error);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <DialogTitle>Nova proposta</DialogTitle>
                <IconButton size="small" onClick={handleClose} sx={{ marginRight: 2 }} aria-label="Fechar">
                    <Close />
                </IconButton>
            </Stack>
            <Divider />
            <DialogContent>
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        Informe os dados iniciais para abrir a proposta. O restante será preenchido na página de detalhe.
                    </Typography>

                    <TextField
                        label="Nome do contato"
                        {...getFieldProps('contractorName')}
                        onBlur={handleBlur}
                        fullWidth
                        error={!!errors.contractorName}
                        helperText={errors.contractorName}
                    />
                    <TextField
                        label="CNPJ"
                        {...getFieldProps('document')}
                        fullWidth
                        onBlur={handleBlur}
                        InputProps={{
                            inputComponent: DocumentInputMasked,
                            inputProps: { documentType: 'cnpj' },
                        }}
                        error={!!errors.document}
                        helperText={errors.document}
                    />
                    <TextField
                        label="Email"
                        {...getFieldProps('contractorEmail')}
                        onBlur={handleBlur}
                        fullWidth
                        error={!!errors.contractorEmail}
                        helperText={errors.contractorEmail}
                    />
                    <TextField
                        label="Cargo"
                        {...getFieldProps('contractorPosition')}
                        fullWidth
                        onBlur={handleBlur}
                        error={!!errors.contractorPosition}
                        helperText={errors.contractorPosition}
                    />
                    <TextField
                        label="Telefone"
                        {...getFieldProps('contractorPhone')}
                        fullWidth
                        onBlur={handleBlur}
                        error={!!errors.contractorPhone}
                        helperText={errors.contractorPhone}
                        InputProps={{
                            inputComponent: PhoneInputMasked,
                        }}
                    />
                    <SelectPlan {...getFieldProps('planId')} fullWidth error={!!errors.planId} helperText={errors.planId} />

                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle2" gutterBottom>
                                Clube existente
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Preencha apenas se for atualizar um clube com novo contrato.
                            </Typography>
                            <SelectClub
                                title="Selecione o clube"
                                name="clubId"
                                value={values.clubId}
                                onChange={handleClubChange}
                                fullWidth
                                error={!!errors.clubId}
                                helperText={errors.clubId}
                            />
                        </CardContent>
                    </Card>
                </Stack>
            </DialogContent>
            <Divider />
            <DialogActions>
                <Button variant="outlined" onClick={handleClose} disabled={isSubmitting}>
                    Cancelar
                </Button>
                <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting} loading={isSubmitting}>
                    Criar proposta
                </Button>
            </DialogActions>
        </Dialog>
    );
}
